import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';
import NotesDetail from './NotesDetail.jsx';

const DenseInput = ({ label, children }) => (
  <div className="form-group" style={{ marginBottom: 8 }}>
    {label && (
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </label>
    )}
    {children}
  </div>
);


const DateInput = ({ value, onChange }) => (
  <input
    type="date"
    className="form-control input-sm"
    value={value || ''}
    onChange={(e) => onChange(e.target.value || null)}
  />
);

const NotesBrowser = ({ perPage = 25 }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [q, setQ] = useState('');
  // Owner filter removed per requirements
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Debounce key for search
  const searchKey = useMemo(() => `${q}|${from}|${to}|${page}`, [q, from, to, page]);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = { page, per_page: perPage };
        if (q) params.q = q;
  // Owner filter removed
        if (from) params.from = from;
        if (to) params.to = to;

        const { data } = await axios.get(API_ENDPOINTS.notes.list, { params });
        if (cancel) return;
        setRows(data.data || []);
        setTotal(data.pagination?.total || 0);
        setLastPage(data.pagination?.last_page || 1);
        if (data.data?.length && !selectedId) {
          setSelectedId(data.data[0].id);
        } else if (!data.data?.length) {
          setSelectedId(null);
        }
      } catch (e) {
        if (!cancel) setError('Unable to load notes');
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    const t = setTimeout(load, q ? 250 : 0);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [searchKey]);

  const pages = Math.max(1, lastPage || Math.ceil(total / perPage));

  return (
    <div className="row">
      <div className="col-md-4">
        <div className="panel panel-default">
          <div className="panel-heading">
            <strong>Notes</strong>
          </div>
          <div className="panel-body" style={{ paddingBottom: 8 }}>
            <DenseInput>
              <input
                type="text"
                className="form-control input-sm"
                placeholder="Search title, description, uid…"
                value={q}
                onChange={(e) => {
                  setPage(1);
                  setQ(e.target.value);
                }}
              />
            </DenseInput>
            <div className="row" style={{ marginBottom: 8 }}>
              <div className="col-xs-12 text-right" style={{ paddingTop: 6 }}>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {total} {total === 1 ? 'note' : 'notes'}
                </span>
              </div>
            </div>
            <div className="row" style={{ marginBottom: 8 }}>
              <div className="col-xs-6">
                <DenseInput label="From">
                  <DateInput value={from} onChange={(v) => { setPage(1); setFrom(v); }} />
                </DenseInput>
              </div>
              <div className="col-xs-6">
                <DenseInput label="To">
                  <DateInput value={to} onChange={(v) => { setPage(1); setTo(v); }} />
                </DenseInput>
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="table-responsive" style={{ maxHeight: 480, overflowY: 'auto' }}>
              <table className="table table-condensed table-hover" style={{ marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Title</th>
                    <th>Parent</th>
                    <th className="text-right" style={{ width: 120 }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="text-muted">Loading…</td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-muted">No notes</td>
                    </tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr
                      key={r.id}
                      className={selectedId === r.id ? 'info' : ''}
                      onClick={() => setSelectedId(r.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>
                          {r.title || `Note #${r.id}`}
                        </div>
                        {r.description && (
                          <div className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.description}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.parent ? (
                          r.parent.url ? (
                            <a href={r.parent.url} target="_blank" rel="noreferrer">
                              {r.parent.title} ({r.parent.type_title || r.parent.kind} #{r.parent.id})
                            </a>
                          ) : (
                            <span>
                              {r.parent.title} ({r.parent.type_title || r.parent.kind} #{r.parent.id})
                            </span>
                          )
                        ) : (
                          <span className="text-muted">No parent</span>
                        )}
                      </td>
                      <td className="text-right" style={{ fontSize: 12 }}>
                        {r.created_at && new Date(r.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ margin: 0 }}>
              <div className="col-xs-4" style={{ paddingTop: 6 }}>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Page {page} of {pages}
                </span>
              </div>
              <div className="col-xs-4 text-center">
                <button
                  className="btn btn-default btn-xs"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
              </div>
              <div className="col-xs-4 text-right">
                <button
                  className="btn btn-default btn-xs"
                  disabled={page >= pages || loading}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-8">
        <NotesDetail noteId={selectedId} />
      </div>
    </div>
  );
};

export default NotesBrowser;
